/**
 * ConfigTabContent — Config tab content for the UnifiedBottomPanel.
 *
 * Renders three rows:
 * 1. Reference row: thumbnails + [+] button → opens ReferenceSelector, plus
 *    the style slot (click → StyleLibraryModal, double-click → full-size image)
 * 2. Prompt textarea: full-width, pre-populated, real-time sync
 * 3. Control bar: config pill, retry pill+popover, sparks count, generate button
 *
 * Config flyout popovers per node type:
 * - ImageGenerator: quality (512P/1K/2K/4K) + aspect ratio grid
 * - LocationDesigner: time of day + weather
 * - Character/Object: no config pill, no flyout
 */

import { useCanvasReferencePicker } from '../../CanvasReferencePicking'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactFlow } from 'reactflow'
import { AlertTriangle } from 'lucide-react'
import { ReferenceSelector, type SelectedReference } from './ReferenceSelector'
import { ReferenceRow } from './ReferenceRow'
import { StyleSlot } from './StyleSlot'
import { ReferenceStyleRow } from './ReferenceStyleRow'
import { CanvasRichTextBox, type RichTextBoxState } from './RichTextBox'
import { CREATIVE_TEXT_MAX_LENGTH, logCreativeTextLimitConfigured } from './descriptionLimits'
import { StyleLibraryModal } from './StyleLibraryModal'
import { FlyoutPortal } from './FlyoutPortal'
import { CreditQuantityPill } from './CreditQuantityPill'
import { useClickOutside } from './useClickOutside'
import { useImageModels } from '../../../../hooks/useImageModels'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useUploadFile } from '../../../../contexts/UploadContext'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { getImageModelLabel } from '../../../../constants/modelAliases'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import {
  imageSparksPerGeneration,
  LOCATION_PANORAMA_SOURCE_MODEL_ID,
  locationPanoramaSparksPerGeneration,
  type AspectRatio,
} from '../../../../generated/nodeDataTypes'
import {
  AUTO_ASPECT_RATIO,
  aspectRatioValue,
  FALLBACK_ASPECT_RATIOS,
  getImageConstraints,
  getImageReferenceLimit,
  getImageReservedStyleSlots,
} from './imageConstraints'
import { preflight } from './generationPreflight'
import { Panorama360Icon } from './Panorama360Icon'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'
import {
  buildImageModelOptions,
  resolveImageModelDisplayLabel,
  type ImageModelOption,
} from './imageModelOptions'

const modelLog = createDebug('dw:image-gen:model-select')
// Dev-gated (VITE_NAD_DEBUG) trace of the (model, quality, count) -> sparks
// resolution so the demo timeline shows the number feeding the cost pill.
const sparksLog = createNadDebug('dw:image-pricing:quote')

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConfigNodeType = 'character' | 'location' | 'object' | 'imageGenerator' | 'storyboardDesigner'

/**
 * Map ConfigTabContent's `nodeType` prop to the backend node-type string used
 * to key `YDOC_NODE_FIELDS` (and so determine which reference media-type tabs
 * the picker should expose).
 */
const CONFIG_NODE_TYPE_TO_BACKEND: Record<ConfigNodeType, string> = {
  character: 'character_designer',
  location: 'location_designer',
  object: 'object_designer',
  imageGenerator: 'image_generator',
  storyboardDesigner: 'storyboard_designer',
}

export interface ConfigTabContentProps {
  nodeId: string
  nodeType: ConfigNodeType
  // Prompt
  prompt: string
  onPromptChange: (value: string) => void
  // References
  references: SelectedReference[]
  onReferencesChange: (refs: SelectedReference[]) => void
  // Style
  currentStyleId?: string
  onStyleChange: (styleId: string) => void
  // Node-type-specific config (ImageGenerator)
  aspectRatio?: AspectRatio
  quality?: string
  model?: string
  onAspectRatioChange?: (ratio: AspectRatio) => void
  onQualityChange?: (quality: string) => void
  /** Image Generator: values the last model switch had to change, if any. */
  imageConstraintNotice?: ImageConstraintNotice | null
  onModelChange?: (model: string) => void
  // Node-type-specific config (LocationDesigner)
  timeOfDay?: string
  weather?: string
  onTimeOfDayChange?: (time: string) => void
  onWeatherChange?: (weather: string) => void
  panoramaEnabled?: boolean
  onCreatePanoramaNode?: () => void
  canCreatePanoramaNode?: boolean
  // VLM Quality Check
  vlmValidation?: boolean
  onVlmValidationChange?: (enabled: boolean) => void
  // Execution
  isRunning: boolean
  onGenerate: (retryCount: number) => void
  onCancel?: () => void
  isCancelling?: boolean
  /**
   * Minimum number of images billed per run, used only to compute the sparks
   * quote (NOT the displayed retry pill). character_designer forces
   * `count = max(count, 2)` on its first generation (orchestrator.py), so its
   * node passes `minCount={2}` then to make the quote match the charge (e.g.
   * first character gen at 1x shows 26, not 13 — intentional). Defaults to 1.
   */
  minCount?: number
  maxRetryCount?: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// ImageGenerator flyout options
export const QUALITY_OPTIONS = ['512P', '1K', '2K', '4K']
// Derived, not declared (RFC-1202 phase 4). Per-model gating goes through
// getImageConstraints(model).aspectRatioOptions; this is the model-less grid.
export const ASPECT_RATIO_GRID: readonly AspectRatio[] = FALLBACK_ASPECT_RATIOS
// Fallback chip list, used only while /image-models is in flight or has errored.
// The GPT Image 2.5 variants are deliberately absent: they sit behind the
// `gpt_image_2_5` runtime feature gate, and only the API knows that gate's mode.
// Listing them here would flash gated chips before the real list resolves, so
// this list stays fail-closed and the variants arrive with the API response.
export const IMAGE_MODEL_OPTIONS = [
  'gemini-3.1-flash-lite-image',
  'gemini-3.1-flash-image-preview',
  'openai-gpt-image-2',
].map((value) => ({ value, label: getImageModelLabel(value) }))
const INTERNAL_EVALUATION_IMAGE_MODEL_IDS = new Set<string>(['mango-ember-drift'])
type SegmentedButtonTone = 'default' | 'internal'

function isInternalEvaluationImageModel(model: string): boolean {
  return INTERNAL_EVALUATION_IMAGE_MODEL_IDS.has(model)
}

function imageModelButtonClass(
  isSelected: boolean,
  tone: SegmentedButtonTone = 'default',
  disabled = false,
): string {
  const isInternal = tone === 'internal'
  const stateClasses = isSelected
    ? isInternal
      ? 'bg-primary/20 text-primary border border-agent-sound/80'
      : 'bg-primary/20 text-primary border border-primary/40'
    : isInternal
      ? 'bg-surface-2 text-text-muted border border-agent-sound/60 hover:bg-surface-3 hover:text-foreground hover:border-agent-sound/80'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  const disabledClasses = disabled ? 'cursor-not-allowed opacity-60' : ''
  return `inline-flex flex-1 min-w-0 items-center justify-center py-1.5 rounded-md text-xs font-medium transition-colors ${stateClasses} ${disabledClasses}`
}

// LocationDesigner flyout options
export const TIME_OF_DAY_OPTIONS = ['Dawn', 'Day', 'Dusk', 'Night']
export const WEATHER_OPTIONS = ['Clear', 'Cloudy', 'Rainy', 'Foggy', 'Snowy', 'Stormy']

// The option VALUES above are display-cased, but the canonical stored token is
// LOWERCASE (`in_time_of_day='day'`, `in_weather='clear'` — the LocationDesigner
// wire lowercases on write). These helpers localize only the DISPLAY label,
// keyed by the lowercase token, and fall back to the raw value — mirroring the
// value/label split of localizeCameraPreset / localizePortTypeLabel. The token
// stays the stable id used for filtering, storage, and data-testids.
export function localizeTimeOfDay(
  value: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  return t(`configTabContent.timeOfDayOptions.${value.toLowerCase()}`, { defaultValue: value })
}

export function localizeWeather(
  value: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  return t(`configTabContent.weatherOptions.${value.toLowerCase()}`, { defaultValue: value })
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConfigTabContent = memo(function ConfigTabContent({
  nodeId,
  nodeType,
  prompt,
  onPromptChange,
  references,
  onReferencesChange,
  currentStyleId,
  onStyleChange,
  aspectRatio = '16:9',
  quality = '1K',
  model = 'gemini-3.1-flash-image-preview',
  onAspectRatioChange,
  onQualityChange,
  imageConstraintNotice,
  onModelChange,
  timeOfDay = 'Day',
  weather = 'Clear',
  onTimeOfDayChange,
  onWeatherChange,
  panoramaEnabled,
  onCreatePanoramaNode,
  canCreatePanoramaNode,
  vlmValidation,
  onVlmValidationChange,
  isRunning,
  onGenerate,
  onCancel,
  isCancelling,
  minCount = 1,
  maxRetryCount = 4,
}: ConfigTabContentProps) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const onUploadFile = useUploadFile()
  const { emitNuxEvent } = useNuxTooltipEvents()
  // ── Local state ──────────────────────────────────────────────────────────
  const [retryCount, setRetryCount] = useState(1)
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false)
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  const pillRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)
  const { getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const closeFlyout = useCallback(() => setIsFlyoutOpen(false), [])
  const {
    native: apiImageModels,
    byom: byomImageModels,
    isLoading: imageModelsLoading,
    error: imageModelsError,
  } = useImageModels()

  // ── Computed values ──────────────────────────────────────────────────────
  const imageModelOptions = useMemo<ImageModelOption[]>(() => {
    return buildImageModelOptions(
      apiImageModels,
      byomImageModels,
      IMAGE_MODEL_OPTIONS.map((option) => option.value),
      t('projectConfigGear.myImageModel'),
    )
  }, [apiImageModels, byomImageModels, t])
  const imageModelOptionValues = useMemo(
    () => new Set(imageModelOptions.map((option) => option.value)),
    [imageModelOptions],
  )
  const isLocationPanoramaMode = nodeType === 'location' && Boolean(panoramaEnabled)
  const imageModelAvailabilityKnown = !imageModelsLoading && imageModelsError == null
  const isImageModelUnavailable =
    !isLocationPanoramaMode
    && imageModelAvailabilityKnown
    && Boolean(model.trim())
    && !imageModelOptionValues.has(model)
  const unavailableImageModelMessage = t('configTabContent.unavailableModelMessage')
  const selectedImageCapabilities = byomImageModels.find((entry) => entry.id === model)?.capabilities
  const effectiveImageModel = isLocationPanoramaMode
    ? LOCATION_PANORAMA_SOURCE_MODEL_ID
    : model
  const imageModelLabel = resolveImageModelDisplayLabel(
    effectiveImageModel,
    imageModelOptions,
    isImageModelUnavailable,
    t('configTabContent.unavailableModel'),
    t('configTabContent.modelFallback'),
  )
  // Cost quote = per-image sparks (from google_pricing.json via codegen) ×
  // effective image count. Round-per-image THEN multiply — matches the backend
  // `usd_to_sparks(perImage)` then `estimate.sparks *= count` (orchestrator).
  // effectiveCount honours character_designer's first-gen `max(count, 2)` bump
  // via minCount, while the retry pill still shows the user's chosen retryCount.
  // imageSparksPerGeneration returns 0 for unknown models so the pill degrades
  // to '—' rather than a misleading hardcoded number. 360 Location source
  // generation uses a backend-owned model and exact size, so use its generated
  // mirror instead of the node's hidden normal in_model value.
  const perImageSparks = isLocationPanoramaMode
    ? locationPanoramaSparksPerGeneration()
    : imageSparksPerGeneration(model, quality, aspectRatio)
  const boundedMaxRetryCount = Math.max(1, Math.min(4, maxRetryCount))
  const boundedRetryCount = Math.min(retryCount, boundedMaxRetryCount)
  const effectiveCount = Math.max(boundedRetryCount, minCount)
  const sparksCount = perImageSparks > 0 ? perImageSparks * effectiveCount : undefined
  const retryOptions = useMemo(
    () => [4, 3, 2, 1].filter((count) => count <= boundedMaxRetryCount),
    [boundedMaxRetryCount],
  )
  useEffect(() => {
    sparksLog({
      event: 'config-quote',
      nodeType,
      model,
      quality,
      aspectRatio,
      perImageSparks,
      retryCount: boundedRetryCount,
      effectiveCount,
      sparks: sparksCount ?? null,
    })
  }, [nodeType, model, quality, aspectRatio, perImageSparks, boundedRetryCount, effectiveCount, sparksCount])
  useEffect(() => {
    if (retryCount > boundedMaxRetryCount) {
      setRetryCount(boundedMaxRetryCount)
    }
  }, [boundedMaxRetryCount, retryCount])
  const hasConfigPill = nodeType === 'imageGenerator' || nodeType === 'location' || nodeType === 'character' || nodeType === 'object' || nodeType === 'storyboardDesigner'

  // Drop the quality segment for models that use a fixed resolution so the
  // pill never shows a stale/meaningless quality (e.g. GPT Image, Nano Banana).
  const imageQualityIsFixed = getImageConstraints(model).qualityOptions === null
  // 4 is the historical hard-coded cap, kept only as the BYOM/unknown fallback
  // where no budget is declared — never as a value any declared model uses.
  const imageReferenceLimit = getImageReferenceLimit(model) ?? 4
  const selectCanvasReferences = useCanvasReferencePicker({
    projectId,
    ownerNodeId: nodeId,
    references,
    onChange: onReferencesChange,
    mediaTypes: ['image'],
    maxReferences: imageReferenceLimit,
    mediaLimits: { image: imageReferenceLimit },
  })
  const imageReservedStyleSlots = getImageReservedStyleSlots(model)

  // RFC-1202 phase 4: refuse before the charge, not after it. §2.4 names this
  // button's old condition — `isRunning || isImageModelUnavailable` — as the
  // gap: it could never say "this node is not ready", so an over-cap selection
  // was only discovered by the agent, minutes and one refund later.
  //
  // The contract declares the same resolution wire values as the picker.
  const generationBlockReasons = useMemo(
    () =>
      isLocationPanoramaMode
        ? []
        : preflight(
            {
              modelId: model,
              byomCapabilities: selectedImageCapabilities,
              inputCounts: { in_images: references.length, in_reference_videos: 0, in_reference_audios: 0 },
              parameters: { aspect_ratio: aspectRatio, resolution: quality },
            },
            imageModelLabel,
            "console-image",
          ),
    [isLocationPanoramaMode, model, references.length, aspectRatio, quality, imageModelLabel, selectedImageCapabilities],
  )
  const generationBlockMessage =
    generationBlockReasons.length > 0
      ? t(
          `generation.blockReason.${generationBlockReasons[0].code}`,
          generationBlockReasons[0].params,
        )
      : null
  const aspectRatioLabel =
    aspectRatio === AUTO_ASPECT_RATIO ? t('configTabContent.aspectAuto') : aspectRatio
  const configPillLabel =
    nodeType === 'imageGenerator'
      ? imageQualityIsFixed
        ? `${imageModelLabel} · ${aspectRatioLabel}`
        : `${imageModelLabel} · ${aspectRatioLabel} · ${quality}`
      : nodeType === 'location'
        ? imageModelLabel
        : nodeType === 'character' || nodeType === 'object' || nodeType === 'storyboardDesigner'
          ? imageModelLabel
          : ''

  const closeRetryPopover = useCallback(() => setIsRetryPopoverOpen(false), [])

  useEffect(() => {
    logCreativeTextLimitConfigured('config-tab-prompt', { nodeType })
  }, [nodeType])

  // ── Click-outside for retry popover ───────────────────────────────────────
  useClickOutside([retryPopoverRef, retryPillRef], closeRetryPopover, isRetryPopoverOpen)

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleGenerate = useCallback(() => {
    if (isImageModelUnavailable) return
    // Guard the handler, not just the button: the keyboard path below calls
    // this directly, so a disabled attribute alone would leave a way through.
    if (generationBlockMessage !== null) return
    onGenerate(boundedRetryCount)
  }, [isImageModelUnavailable, generationBlockMessage, onGenerate, boundedRetryCount])

  const handleRefSave = useCallback(
    (selected: SelectedReference[]) => {
      onReferencesChange(selected)
      if (nodeType === 'object') {
        emitNuxEvent({
          type: 'controlActivated',
          controlId: 'addProps',
          anchorId: 'object-add-props',
          nodeId,
          nodeType: 'objectDesigner',
        })
      }
      setIsRefSelectorOpen(false)
    },
    [emitNuxEvent, nodeId, nodeType, onReferencesChange]
  )

  const handleObjectPropsActivated = useCallback(() => {
    if (nodeType !== 'object') return
    emitNuxEvent({
      type: 'controlActivated',
      controlId: 'addProps',
      anchorId: 'object-add-props',
      nodeId,
      nodeType: 'objectDesigner',
    })
  }, [emitNuxEvent, nodeId, nodeType])

  const handleStyleApply = useCallback(
    (styleId: string) => {
      onStyleChange(styleId)
      setIsStyleModalOpen(false)
    },
    [onStyleChange]
  )

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col h-full p-3 gap-2"
      data-testid="config-tab-content"
    >
      {/* Row 1: References | Style, split by a divider (see ReferenceStyleRow).
          The style tile is the only style picker: hover magnifies, click
          opens the style library, double-click shows the full-size image. */}
      <ReferenceStyleRow
        references={
          // Shared with the Refine tab (see ReferenceRow).
          <div
            data-nux-anchor={nodeType === 'object' ? 'object-add-props' : undefined}
            data-nux-node-id={nodeType === 'object' ? nodeId : undefined}
            data-nux-node-type={nodeType === 'object' ? 'objectDesigner' : undefined}
          >
            <ReferenceRow
              references={references}
              projectId={projectId}
              ownerNodeId={nodeId}
              onOpenSelector={() => {
                handleObjectPropsActivated()
                setIsRefSelectorOpen(true)
              }}
              onSelectCanvas={() => {
                handleObjectPropsActivated()
                selectCanvasReferences()
              }}
              onReferencesChange={(nextReferences) => {
                handleObjectPropsActivated()
                onReferencesChange(nextReferences)
              }}
            />
          </div>
        }
        style={<StyleSlot currentStyleId={currentStyleId} onOpen={() => setIsStyleModalOpen(true)} />}
      />

      {/* Row 2: Prompt — grows to fill the fixed-height panel so the control
          bar pins to the bottom and no trailing gap remains below it
          (post 1533007285123236). flexFill lets the editor own its height via
          flex layout (scrolls long prompts internally) instead of the
          content-driven max-h cap that previously left empty space. */}
      <div className="flex-1 flex flex-col min-h-0">
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1">
          {t('configTabContent.prompt')}
        </label>
        <CanvasRichTextBox
        flexFill
        containerClassName="flex-1 min-h-[60px] overflow-hidden w-full bg-surface-2 border border-border-subtle rounded-lg px-3 py-2 transition-colors focus-within:border-primary/50 nodrag nowheel"
        containerTestId="config-prompt-textarea"
        referenceNodeIds={references.map(reference => reference.nodeId)}
        referenceOwnerNodeId={nodeId}
        referenceOwnerNodeType={nodeType}
        placeholder={`${t(`configTabContent.placeholder${nodeType.charAt(0).toUpperCase()}${nodeType.slice(1)}`)}\n${t('configTabContent.submitShortcutHint')}`}
        initialContent={prompt}
        onContentChange={(state: RichTextBoxState) => {
          logCreativeTextLimitConfigured('config-tab-prompt', {
            nodeType,
            textLength: state.plainText.length,
          })
          onPromptChange(state.plainText)
        }}
        onSubmit={() => {
          if (!isRunning && !isImageModelUnavailable && generationBlockMessage === null) {
            handleGenerate()
          }
        }}
        submitOnEnter={false}
        submitOnShiftEnter
        minHeight={40}
        maxHeight={120}
        maxLength={CREATIVE_TEXT_MAX_LENGTH}
        />
      </div>

      {isImageModelUnavailable && (
        <div
          className="flex items-start gap-1.5 rounded-lg border border-warning/30 bg-warning/10 px-2 py-1.5 text-xs font-medium text-warning"
          role="alert"
          aria-live="polite"
          data-testid="image-model-unavailable-alert"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{unavailableImageModelMessage}</span>
        </div>
      )}

      {/* Row 3: Control Bar */}
      <div className="flex items-center gap-2" data-testid="control-bar">
        {/* Config Pill (conditional) */}
        {hasConfigPill && (
          <>
            <button
              ref={pillRef}
              onClick={() => setIsFlyoutOpen(!isFlyoutOpen)}
              className="px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
              data-testid="config-pill"
            >
              {configPillLabel}
            </button>

            {/* Config Flyout Popover — portal-based */}
            <FlyoutPortal
              triggerRef={pillRef}
              isOpen={isFlyoutOpen}
              onClose={closeFlyout}
            >
              {nodeType === 'imageGenerator' && (
                <ImageGeneratorFlyout
                  quality={quality}
                  aspectRatio={aspectRatio}
                  model={model}
                  modelOptions={imageModelOptions}
                  onQualityChange={(q) => onQualityChange?.(q)}
                  onAspectRatioChange={(r) => onAspectRatioChange?.(r)}
                  onModelChange={(m) => onModelChange?.(m)}
                  notice={imageConstraintNotice ?? null}
                />
              )}
              {nodeType === 'location' && (
                <LocationFlyout
                  timeOfDay={timeOfDay}
                  weather={weather}
                  onTimeOfDayChange={(v) => onTimeOfDayChange?.(v)}
                  onWeatherChange={(w) => onWeatherChange?.(w)}
                  model={model}
                  modelOptions={imageModelOptions}
                  onModelChange={(m) => onModelChange?.(m)}
                  panoramaEnabled={panoramaEnabled}
                  showModelSelector={!isLocationPanoramaMode}
                  onCreatePanoramaNode={onCreatePanoramaNode}
                  canCreatePanoramaNode={canCreatePanoramaNode}
                />
              )}
              {(nodeType === 'character' || nodeType === 'object' || nodeType === 'storyboardDesigner') && (
                <ModelOnlyFlyout
                  model={model}
                  modelOptions={imageModelOptions}
                  onModelChange={(m) => onModelChange?.(m)}
                />
              )}
            </FlyoutPortal>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* VLM Quality Check Toggle */}
        {onVlmValidationChange && (
          <label
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
            data-testid="vlm-validation-toggle"
          >
            <input
              type="checkbox"
              checked={vlmValidation ?? false}
              onChange={(e) => onVlmValidationChange(e.target.checked)}
              className="w-3 h-3 rounded border-border accent-primary cursor-pointer"
            />
            {t('configTabContent.qc')}
          </label>
        )}

        {/* Credit estimate + run count — one pill, per frame 137:1507. */}
        <CreditQuantityPill sparks={sparksCount} sparksTestId="sparks-count">
          <div className="relative" data-testid="retry-selector">
            <button
              ref={retryPillRef}
              onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
              className="px-1 text-xs font-medium text-text-muted hover:text-foreground transition-colors whitespace-nowrap tabular-nums"
              data-testid="retry-pill"
            >
              {retryCount}x
            </button>

            {isRetryPopoverOpen && (
              <div
                ref={retryPopoverRef}
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                data-testid="retry-popover"
              >
                {retryOptions.map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      setRetryCount(n)
                      setIsRetryPopoverOpen(false)
                    }}
                    className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                      retryCount === n
                        ? 'text-primary-foreground'
                        : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                    }`}
                    style={
                      retryCount === n
                        ? { backgroundColor: 'var(--primary)' }
                        : undefined
                    }
                    data-testid={`retry-${n}x`}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            )}
          </div>
        </CreditQuantityPill>

        {generationBlockMessage !== null && !isRunning && (
          <span
            className="text-xxs text-warning max-w-[220px] leading-tight"
            data-testid="generation-block-reason"
            title={generationBlockMessage}
          >
            {generationBlockMessage}
          </span>
        )}

        {/* Generate / Stop Button */}
        {isRunning && onCancel ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
            disabled={isCancelling}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-destructive/50 bg-destructive/15 text-destructive hover:bg-destructive/25 hover:border-destructive transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={isCancelling ? t('configTabContent.stopping') : t('configTabContent.stop')}
            data-testid="cancel-node-button"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="1" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={isRunning || isImageModelUnavailable || generationBlockMessage !== null}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              isImageModelUnavailable
                ? unavailableImageModelMessage
                : (generationBlockMessage ?? t('configTabContent.generate'))
            }
            data-testid="generate-button"
          >
            {/* Arrow-up icon */}
            <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        )}
      </div>

      {/* Reference Selector Modal */}
      <ReferenceSelector
        isOpen={isRefSelectorOpen}
        onClose={() => setIsRefSelectorOpen(false)}
        onSave={handleRefSave}
        excludeNodeId={nodeId}
        canvasNodes={getNodes()}
        currentReferences={references}
        // Was a hard-coded 4, which matched no model: real budgets are 9-15
        // user references. Reads the generated capability mirror, falling back
        // to 4 only for BYOM / unknown ids where nothing is declared
        // (CLAUDE.md #8) so behaviour there is unchanged.
        maxReferences={imageReferenceLimit}
        // Names the gap between the provider budget and the user cap, so 15/16
        // reads as a reservation rather than an arbitrary Dreamwrite number.
        reservedStyleSlots={imageReservedStyleSlots}
        // refuseAtMediaLimit keys off the PER-MEDIA cap, not maxReferences, so
        // the image limit has to be declared here too or the refusal is a
        // no-op. This picker only shows the image tab.
        maxReferencesByMediaType={{ image: imageReferenceLimit }}
        // Refuse a pick at the cap instead of silently evicting the user's
        // oldest reference, matching the video picker.
        refuseAtMediaLimit
        visibleTabs={['image']}
        targetBackendType={CONFIG_NODE_TYPE_TO_BACKEND[nodeType]}
        onUploadFile={onUploadFile ?? undefined}
        onUploadCompleteReferencesChange={onReferencesChange}
      />

      {/* Style Library Modal */}
      <StyleLibraryModal
        isOpen={isStyleModalOpen}
        onClose={() => setIsStyleModalOpen(false)}
        onApply={handleStyleApply}
        currentStyleId={currentStyleId ?? ''}
      />
    </div>
  )
})

ConfigTabContent.displayName = 'ConfigTabContent'

// ---------------------------------------------------------------------------
// ImageGenerator Flyout
// ---------------------------------------------------------------------------

/**
 * What the last model switch had to change because the new model cannot
 * fulfil the user's choice. The node keeps the original as intent, so switching
 * back restores it.
 */
export interface ImageConstraintNotice {
  model: string
  aspect?: { from: AspectRatio; to: AspectRatio }
  quality?: { from: string; to: string }
}

interface ImageGeneratorFlyoutProps {
  quality: string
  aspectRatio: AspectRatio
  model: string
  modelOptions: ImageModelOption[]
  onQualityChange: (quality: string) => void
  onAspectRatioChange: (ratio: AspectRatio) => void
  onModelChange: (model: string) => void
  notice: ImageConstraintNotice | null
}

const ASPECT_SHAPE_BOX = 14

/** Small proportional rectangle previewing a ratio; a dashed square for Auto. */
function AspectShape({ ratio }: { ratio: AspectRatio }) {
  const value = aspectRatioValue(ratio)
  if (value === null) {
    return (
      <span
        aria-hidden
        className="inline-block rounded-[2px] border border-dashed border-current"
        style={{ width: ASPECT_SHAPE_BOX, height: ASPECT_SHAPE_BOX }}
      />
    )
  }
  const width = value >= 1 ? ASPECT_SHAPE_BOX : Math.round(ASPECT_SHAPE_BOX * value)
  const height = value >= 1 ? Math.round(ASPECT_SHAPE_BOX / value) : ASPECT_SHAPE_BOX
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center"
      style={{ width: ASPECT_SHAPE_BOX, height: ASPECT_SHAPE_BOX }}
    >
      <span className="block rounded-[2px] border border-current" style={{ width, height }} />
    </span>
  )
}

function aspectRatioDisplay(ratio: AspectRatio, autoLabel: string): string {
  return ratio === AUTO_ASPECT_RATIO ? autoLabel : ratio
}

const ImageGeneratorFlyout = memo(function ImageGeneratorFlyout({
  quality,
  aspectRatio,
  model,
  modelOptions,
  onQualityChange,
  onAspectRatioChange,
  onModelChange,
  notice,
}: ImageGeneratorFlyoutProps) {
  const { t } = useTranslation('canvas')
  const handleModelChange = useCallback(
    (value: string) => {
      modelLog('model change: %s -> %s', model, value)
      onModelChange(value)
    },
    [model, onModelChange],
  )

  // Per-model capability gating: only offer the quality/aspect options the
  // selected model can actually fulfil (mirrors backend capability SSOT).
  const constraints = getImageConstraints(model)
  const autoLabel = t('configTabContent.aspectAuto')

  return (
    <div className="flex flex-col gap-3" data-testid="image-generator-flyout">
      {/* Model */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          {t('configTabContent.model')}
        </label>
        <div className="flex flex-wrap gap-1" data-testid="model-selector">
          {modelOptions.map((opt) => {
            const isInternalModel = isInternalEvaluationImageModel(opt.value)
            return (
              <button
                key={opt.value}
                type="button"
                title={isInternalModel ? t('configTabContent.internalEvaluationModel') : undefined}
                onClick={() => handleModelChange(opt.value)}
                className={imageModelButtonClass(
                  model === opt.value,
                  isInternalModel ? 'internal' : 'default',
                )}
                data-internal-model={isInternalModel ? 'true' : undefined}
                data-testid={`model-${opt.value}`}
              >
                <span className="truncate">{opt.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Quality — a static label for models that use a fixed resolution */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          {t('configTabContent.quality')}
        </label>
        {constraints.qualityOptions === null ? (
          <span
            className="inline-block text-xs font-medium text-text-muted"
            data-testid="quality-static"
            title={t('configTabContent.fixedResolutionTooltip')}
          >
            {constraints.qualityLabel ?? t('configTabContent.fixed')}
          </span>
        ) : (
          <div className="flex gap-1">
            {constraints.qualityOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => onQualityChange(opt)}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  quality === opt
                    ? 'bg-primary/20 text-primary border border-primary/40'
                    : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
                }`}
                data-testid={`quality-${opt}`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Aspect Ratio — only the ratios the selected model can fulfil */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          {t('configTabContent.aspectRatio')}
        </label>
        <div className="grid grid-cols-5 gap-1" data-testid="aspect-ratio-grid">
          {constraints.aspectRatioOptions.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onAspectRatioChange(opt)}
              title={opt === AUTO_ASPECT_RATIO ? t('configTabContent.aspectAutoTooltip') : undefined}
              className={`flex flex-col items-center gap-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                aspectRatio === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`ratio-${opt}`}
            >
              <AspectShape ratio={opt} />
              <span>{aspectRatioDisplay(opt, autoLabel)}</span>
            </button>
          ))}
        </div>
      </div>

      {notice && notice.model === model && (notice.aspect || notice.quality) && (
        <div
          className="flex flex-col gap-0.5 rounded-md border border-warning/40 bg-warning/10 px-2 py-1.5 text-xxs text-warning"
          role="status"
          data-testid="image-constraint-notice"
        >
          {notice.aspect && (
            <span data-testid="image-constraint-notice-aspect">
              {t('configTabContent.constraintNoticeAspect', {
                from: aspectRatioDisplay(notice.aspect.from, autoLabel),
                to: aspectRatioDisplay(notice.aspect.to, autoLabel),
              })}
            </span>
          )}
          {notice.quality && (
            <span data-testid="image-constraint-notice-quality">
              {t('configTabContent.constraintNoticeQuality', {
                from: notice.quality.from,
                to: notice.quality.to,
              })}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

ImageGeneratorFlyout.displayName = 'ImageGeneratorFlyout'

// ---------------------------------------------------------------------------
// LocationDesigner Flyout
// ---------------------------------------------------------------------------

interface LocationFlyoutProps {
  timeOfDay: string
  weather: string
  onTimeOfDayChange: (time: string) => void
  onWeatherChange: (weather: string) => void
  model: string
  modelOptions: ImageModelOption[]
  onModelChange: (model: string) => void
  panoramaEnabled?: boolean
  showModelSelector?: boolean
  onCreatePanoramaNode?: () => void
  canCreatePanoramaNode?: boolean
}

const LocationFlyout = memo(function LocationFlyout({
  timeOfDay,
  weather,
  onTimeOfDayChange,
  onWeatherChange,
  model,
  modelOptions,
  onModelChange,
  panoramaEnabled = false,
  showModelSelector = true,
  onCreatePanoramaNode,
  canCreatePanoramaNode = true,
}: LocationFlyoutProps) {
  const { t } = useTranslation('canvas')
  const handleModelChange = useCallback(
    (value: string) => {
      modelLog('model change: %s -> %s', model, value)
      onModelChange(value)
    },
    [model, onModelChange],
  )

  return (
    <div className="flex flex-col gap-3" data-testid="location-flyout">
      {(panoramaEnabled || onCreatePanoramaNode) && (
        <div>
          <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
            {t('locationDesignerNode.panoramaTitle')}
          </label>
          {panoramaEnabled ? (
            <div
              className="inline-flex h-7 items-center rounded-lg border border-primary/40 bg-primary/20 px-2.5 text-xs font-medium text-primary"
              data-testid="location-panorama-status"
            >
              {t('locationDesignerNode.panorama720')}
            </div>
          ) : (
            <button
              type="button"
              onClick={onCreatePanoramaNode}
              disabled={!canCreatePanoramaNode}
              className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="location-create-720"
            >
              <Panorama360Icon className="h-3.5 w-3.5" />
              {t('locationDesignerNode.createPanorama')}
            </button>
          )}
        </div>
      )}

      {showModelSelector && (
        <div>
          <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
            {t('configTabContent.model')}
          </label>
          <div className="flex flex-wrap gap-1" data-testid="model-selector">
            {modelOptions.map((opt) => {
              const isInternalModel = isInternalEvaluationImageModel(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  title={isInternalModel ? t('configTabContent.internalEvaluationModel') : undefined}
                  onClick={() => handleModelChange(opt.value)}
                  className={imageModelButtonClass(
                    model === opt.value,
                    isInternalModel ? 'internal' : 'default',
                  )}
                  data-internal-model={isInternalModel ? 'true' : undefined}
                  data-testid={`model-${opt.value}`}
                >
                  <span className="truncate">{opt.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Time of Day */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          {t('configTabContent.timeOfDay')}
        </label>
        <div className="flex gap-1">
          {TIME_OF_DAY_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onTimeOfDayChange(opt)}
              className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                timeOfDay === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`time-${opt}`}
            >
              {localizeTimeOfDay(opt, t)}
            </button>
          ))}
        </div>
      </div>

      {/* Weather */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          {t('configTabContent.weather')}
        </label>
        <div className="grid grid-cols-3 gap-1">
          {WEATHER_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onWeatherChange(opt)}
              className={`py-1.5 rounded-md text-xs font-medium transition-colors ${
                weather === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`weather-${opt}`}
            >
              {localizeWeather(opt, t)}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
})

LocationFlyout.displayName = 'LocationFlyout'

// ---------------------------------------------------------------------------
// Model-Only Flyout (Character / Object)
// ---------------------------------------------------------------------------

export interface ModelOnlyFlyoutProps {
  model: string
  modelOptions: ImageModelOption[]
  onModelChange: (model: string) => void
}

export const ModelOnlyFlyout = memo(function ModelOnlyFlyout({
  model,
  modelOptions,
  onModelChange,
}: ModelOnlyFlyoutProps) {
  const { t } = useTranslation('canvas')
  const handleModelChange = useCallback(
    (value: string) => {
      modelLog('model change: %s -> %s', model, value)
      onModelChange(value)
    },
    [model, onModelChange],
  )

  return (
    <div className="flex flex-col gap-3" data-testid="model-only-flyout">
      {/* Model */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          {t('configTabContent.model')}
        </label>
        <div className="flex flex-wrap gap-1" data-testid="model-selector">
          {modelOptions.map((opt) => {
            const isInternalModel = isInternalEvaluationImageModel(opt.value)
            return (
              <button
                key={opt.value}
                type="button"
                title={isInternalModel ? t('configTabContent.internalEvaluationModel') : undefined}
                onClick={() => handleModelChange(opt.value)}
                className={imageModelButtonClass(
                  model === opt.value,
                  isInternalModel ? 'internal' : 'default',
                )}
                data-internal-model={isInternalModel ? 'true' : undefined}
                data-testid={`model-${opt.value}`}
              >
                <span className="truncate">{opt.label}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
})

ModelOnlyFlyout.displayName = 'ModelOnlyFlyout'

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { ConfigTabContent }
